class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

function normalizeEuVatId(value) {
  if (window.PantheraVat?.normalize) return window.PantheraVat.normalize(value);
  return String(value || '')
    .toUpperCase()
    .replace(/[\s.\-\/]/g, '')
    .replace(/(?:MWST|TVA|IVA|VAT)$/, '');
}

function isValidEuVatId(value) {
  if (window.PantheraVat?.isValid) return window.PantheraVat.isValid(value);
  return /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(normalizeEuVatId(value));
}

if (!customElements.get('cart-order-type')) {
  customElements.define(
    'cart-order-type',
    class CartOrderType extends HTMLElement {
      connectedCallback() {
        this.vatField = this.querySelector('[data-vat-field]');
        this.vatInput = this.querySelector('[name="attributes[vat-id]"]');
        this.vatError = this.querySelector('[data-vat-error]');
        this.onChange = this.onChange.bind(this);
        this.onVatInput = debounce(this.onVatInput.bind(this), ON_CHANGE_DEBOUNCE_TIMER);
        this.addEventListener('change', this.onChange);
        this.vatInput?.addEventListener('input', this.onVatInput);
        this.applyUi();
        this.syncCheckoutButtons();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.vatInput?.removeEventListener('input', this.onVatInput);
      }

      orderType() {
        return this.querySelector('[name="attributes[Bestellart]"]:checked')?.value || 'Privat';
      }

      isBusiness() {
        return this.orderType() === 'Geschäftlich';
      }

      isValid() {
        return !this.isBusiness() || isValidEuVatId(this.vatInput?.value);
      }

      onChange(event) {
        if (event.target.name === 'attributes[Bestellart]' && !this.isBusiness() && this.vatInput) {
          this.vatInput.value = '';
        }
        this.applyUi();
        this.mirrorSiblings();
        this.syncCheckoutButtons();
        this.persist();
      }

      onVatInput() {
        this.applyUi();
        this.mirrorSiblings();
        this.syncCheckoutButtons();
        this.persist();
      }

      applyUi() {
        const business = this.isBusiness();
        const invalid = business && Boolean(this.vatInput?.value) && !this.isValid();
        if (this.vatInput) {
          this.vatInput.required = business;
          this.vatInput.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        }
        if (this.vatError) {
          this.vatError.hidden = !invalid;
        }
      }

      mirrorSiblings() {
        const type = this.orderType();
        const vat = this.isBusiness() ? this.vatInput?.value || '' : '';
        document.querySelectorAll('cart-order-type').forEach((other) => {
          if (other === this) return;
          const radio = other.querySelector(`[name="attributes[Bestellart]"][value="${type}"]`);
          if (radio) radio.checked = true;
          const vatInput = other.querySelector('[name="attributes[vat-id]"]');
          if (vatInput) vatInput.value = vat;
          other.applyUi();
        });
      }

      persist() {
        const business = this.isBusiness();
        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            attributes: {
              Bestellart: this.orderType(),
              'vat-id': business ? String(this.vatInput?.value || '').trim() : '',
              'USt-IdNr': '',
            },
          }),
        });
      }

      cartIsEmpty() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) return drawer.classList.contains('is-empty');
        const items = document.querySelector('cart-items');
        return Boolean(items?.classList.contains('is-empty'));
      }

      syncCheckoutButtons() {
        const block = this.cartIsEmpty() || !this.isValid();
        // ponytail: wallets skip our form; hide on Geschäftlich so vat-id only rides regular checkout
        const hideExpress = this.isBusiness() || !this.isValid();
        document.querySelectorAll('.cart__checkout-button').forEach((button) => {
          button.disabled = block;
        });
        document.documentElement.toggleAttribute('data-cart-hide-express', hideExpress);
        document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
          el.hidden = hideExpress;
        });
      }
    }
  );
}

// ponytail: Cart AJAX discount param (May 2025+); invalid codes often 200 with no apply — we detect via discount_codes / applications
if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[name="discount"]');
        this.message = this.querySelector('.cart-discount__message');
        this.submitBtn = this.querySelector('.cart-discount__submit');
        this.submitBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          this.applyDiscount();
        });
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.applyDiscount();
          }
        });
      }

      getSectionsToRender() {
        if (this.closest('cart-drawer')) {
          return [
            { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
            { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
          ];
        }

        const footer = document.getElementById('main-cart-footer');
        const items = document.getElementById('main-cart-items');
        return [
          {
            id: 'main-cart-items',
            section: items?.dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'main-cart-footer',
            section: footer?.dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ].filter((s) => s.section);
      }

      normalizeCode(value) {
        // Keep leading # — some store codes are literally "#NOW" / "#PANTHERA"
        return (value || '').trim();
      }

      setMessage(text, isError) {
        if (!this.message) return;
        if (!text) {
          this.message.hidden = true;
          this.message.textContent = '';
          this.message.classList.remove('cart-discount__message--error');
          return;
        }
        this.message.hidden = false;
        this.message.textContent = text;
        this.message.classList.toggle('cart-discount__message--error', Boolean(isError));
        this.message.setAttribute('role', isError ? 'alert' : 'status');
      }

      codeApplied(state, code) {
        const upper = (code || '').toUpperCase();
        const upperBare = upper.replace(/^#/, '');
        const matches = (value) => {
          const v = (value || '').toUpperCase();
          return v === upper || v === upperBare || v.replace(/^#/, '') === upperBare;
        };

        if (Array.isArray(state.discount_codes)) {
          const entry = state.discount_codes.find((d) => matches(d.code));
          if (entry) return entry.applicable !== false;
        }
        const apps = state.cart_level_discount_applications || [];
        if (
          apps.some((d) => {
            const title = d.title || d.discount_application?.title || '';
            return matches(title) || title.toUpperCase().includes(upperBare);
          })
        )
          return true;
        return (state.items || []).some((item) =>
          (item.line_level_discount_allocations || []).some((a) => {
            const title = a.discount_application?.title || '';
            return matches(title) || title.toUpperCase().includes(upperBare);
          })
        );
      }

      async updateDiscount(code, sections) {
        const payload = { discount: code };
        if (sections?.length) {
          payload.sections = sections.map((s) => s.section);
          payload.sections_url = window.location.pathname;
        }
        const response = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        });
        return response.json();
      }

      applyDiscount() {
        let code = this.normalizeCode(this.input?.value);
        if (!code) {
          this.input?.focus();
          return;
        }

        this.setMessage('');
        this.submitBtn?.setAttribute('disabled', 'disabled');
        this.classList.add('cart-discount--loading');

        const sections = this.getSectionsToRender();

        this.updateDiscount(code, sections)
          .then(async (state) => {
            if (state.errors || state.description) {
              this.setMessage(state.description || state.message || window.cartStrings.discountError, true);
              return null;
            }

            // Codes like "#NOW" are stored with hash — retry if bare code failed
            if (!this.codeApplied(state, code) && !code.startsWith('#')) {
              const hashed = `#${code}`;
              const retry = await this.updateDiscount(hashed, sections);
              if (!(retry.errors || retry.description) && this.codeApplied(retry, hashed)) {
                code = hashed;
                return retry;
              }
            }

            return state;
          })
          .then((state) => {
            if (!state) return;

            const applied = this.codeApplied(state, code);

            sections.forEach((section) => {
              const el = document.getElementById(section.id);
              if (!el || !state.sections?.[section.section]) return;
              const target = el.querySelector(section.selector) || el;
              const html = new DOMParser()
                .parseFromString(state.sections[section.section], 'text/html')
                .querySelector(section.selector);
              if (target && html) target.innerHTML = html.innerHTML;
            });

            try {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: state });
            } catch (e) {
              /* ignore pub-sub gaps */
            }

            const host =
              document.querySelector('#main-cart-footer cart-discount') ||
              document.querySelector('cart-drawer cart-discount') ||
              document.querySelector('cart-discount');
            if (host) {
              if (applied) {
                const input = host.querySelector('[name="discount"]');
                if (input) input.value = '';
              }
              host.setMessage(
                applied ? window.cartStrings.discountApplied : window.cartStrings.discountError,
                !applied
              );
            }
          })
          .catch(() => {
            this.setMessage(window.cartStrings.error, true);
          })
          .finally(() => {
            document.querySelectorAll('cart-discount').forEach((el) => {
              el.classList.remove('cart-discount--loading');
              el.querySelector('.cart-discount__submit')?.removeAttribute('disabled');
            });
          });
      }
    }
  );
}
